import React, { useState } from 'react'
import { useAdmin } from '../../contexts/AdminContext'
import Heading from '../../components/Heading'
import Button from '../../components/Button'
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons'
import IconButton from '../../components/IconButton'
import { useTranslation } from 'react-i18next'
import Input from '../../components/Input'
import Card from '../../components/Card'
import Text from '../../components/Text'
import CodeInput from '../../components/CodeInput'
import { showToast } from '../../services/ToastService'
import { useNavigate } from 'react-router-dom'

const SubscriptionPasswordReset: React.FC = () => {
    const { service } = useAdmin()
    const { t } = useTranslation(['settings'])
    const [email, setEmail] = useState<string>('')
    const [form, setForm] = useState<'email' | 'code'>('email')
    const navigate = useNavigate()

    const [resetData, setResetData] = useState<
        Partial<{
            token: string
            password: string
            password_confirmation: string
        }>
    >({})

    const submit = () => {
        service
            .passwordReset({
                email: email,
            })
            .then(() => {
                setEmail('')
                showToast(t('subscription.passwordReset.success'))
                setForm('code')
            })
            .catch(() => {
                showToast(t('subscription.passwordReset.error'), 'error')
            })
    }

    const submitCode = () => {
        service
            .passwordResetConfirm(resetData)
            .then(() => {
                showToast(t('subscription.passwordReset.changeSuccess'))
                navigate('/subscription')
            })
            .catch(() => {
                showToast(t('subscription.passwordReset.changeError'), 'error')
            })
    }

    return (
        <div className={'zr-mt-3'}>
            <Heading level={3} classes={'zr-mb-4'}>
                {t('subscription.passwordReset.heading')}
            </Heading>
            <IconButton
                icon={faArrowLeft}
                color={'black'}
                onClick={() => navigate('/subscription')}
            >
                {t('subscription.back')}
            </IconButton>
            {form === 'email' ? (
                <>
                    <Card
                        title={t('subscription.passwordReset.resetText')}
                        classes={'zr-my-3'}
                    >
                        <Text>
                            {t('subscription.passwordReset.description')}
                        </Text>
                        <div className={'zr-row'}>
                            <div className={'zr-col-12 zr-my-3'}>
                                <Input
                                    type={'email'}
                                    placeholder={'E-Mail'}
                                    property={'email'}
                                    value={email}
                                    onChange={(_, value) => setEmail(value)}
                                />
                            </div>
                        </div>
                    </Card>
                    <div className={'zr-mt-4 zr-d-flex zr-justify-between'}>
                        <Button
                            hierarchy={'tertiary'}
                            text={t(
                                'subscription.passwordReset.buttons.alreadyHaveCode'
                            )}
                            fn={() => setForm('code')}
                        />
                        <Button
                            hierarchy={'primary'}
                            text={t(
                                'subscription.passwordReset.buttons.confirmReset'
                            )}
                            fn={() => submit()}
                        />
                    </div>
                </>
            ) : (
                <>
                    <Card
                        title={t('subscription.passwordReset.newPasswordText')}
                        classes={'zr-my-3'}
                    >
                        <div className={'zr-row'}>
                            <div
                                style={{ gap: '2rem' }}
                                className={
                                    'zr-d-flex zr-flex-column zr-col-12 zr-my-3'
                                }
                            >
                                <CodeInput
                                    property={'token'}
                                    value={resetData.token ?? ''}
                                    label={'Reset-Code'}
                                    onChange={(property, value) =>
                                        setResetData((prev) => {
                                            return {
                                                ...prev,
                                                [property]: value,
                                            }
                                        })
                                    }
                                />
                                <Input
                                    type={'password'}
                                    placeholder={t(
                                        'subscription.passwordReset.password'
                                    )}
                                    property={'password'}
                                    value={resetData.password ?? ''}
                                    onChange={(property, value) =>
                                        setResetData((prev) => {
                                            return {
                                                ...prev,
                                                [property]: value,
                                            }
                                        })
                                    }
                                />
                                <Input
                                    type={'password'}
                                    placeholder={t(
                                        'subscription.passwordReset.passwordConfirmation'
                                    )}
                                    property={'password_confirmation'}
                                    value={
                                        resetData.password_confirmation ?? ''
                                    }
                                    onChange={(property, value) =>
                                        setResetData((prev) => {
                                            return {
                                                ...prev,
                                                [property]: value,
                                            }
                                        })
                                    }
                                />
                            </div>
                        </div>
                    </Card>
                    <div className={'zr-mt-4 zr-d-flex zr-justify-between'}>
                        <Button
                            hierarchy={'tertiary'}
                            text={t(
                                'subscription.passwordReset.buttons.noCode'
                            )}
                            fn={() => setForm('email')}
                        />
                        <Button
                            hierarchy={'primary'}
                            text={t(
                                'subscription.passwordReset.buttons.confirmPassword'
                            )}
                            fn={() => submitCode()}
                            disabled={
                                resetData.token === undefined ||
                                resetData.password === undefined ||
                                resetData.password_confirmation === undefined ||
                                resetData.token === '' ||
                                resetData.password === '' ||
                                resetData.password_confirmation === '' ||
                                resetData.password !==
                                    resetData.password_confirmation ||
                                resetData.password.length < 8
                            }
                        />
                    </div>
                </>
            )}
        </div>
    )
}

export default SubscriptionPasswordReset
